import { ChartDonut, ChartThemeColor, ChartTooltip } from "@patternfly/react-charts";
import "./LeafProgressCircle.scss";
import { useEffect, useRef } from "react";
import { formatAboBV } from "../../../components/Lit/acc-mixins/l10n.js";
import LeafCircleLoader from "../LeafLoadingSkeleton/LeafCircleLoader/LeafCircleLoader";
import { useAppSelector } from "../../../store";

export type FormattedLabelValues = {
  currentAmount?: string;
  targetAmount?: string;
};

export type Props = {
  ariaTitle: string;
  subtitle?: string;
  currentAmount: number;
  targetAmount: number;
  isLoading: boolean;
  labels?: Array<string>;
  animationDuration?: number;
  showLabelsOnly?: boolean;
  met?: boolean;
};

const LeafProgressCircle = (props: Props) => {
  const {
    subtitle = "",
    currentAmount,
    targetAmount,
    isLoading,
    labels = [undefined, undefined],
    animationDuration = 0,
    showLabelsOnly = true,
    ariaTitle,
    met,
  } = props;
  const { user } = useAppSelector((state) => state.boot);
  const local = useAppSelector((state) => state.loginLocalStorage.loginFields.locale) || "en-US";
  const ref = useRef(null);
  const clamp = (num: number, min: number, max: number) => Math.min(Math.max(num, min), max);
  const percentage = met ? 1 : currentAmount / targetAmount;
  const currentPercentage = clamp(percentage, 0, 1);
  const renderLabels = (datum: any) => {
    if ((labels[0] !== undefined || labels[1] !== undefined) && labels.length === 2) {
      if (currentAmount === 0) {
        return `${currentAmount}`;
      }
      if (showLabelsOnly) {
        return `${datum.x}`;
      }
      return `${datum.x}: ${datum.y}`;
    }

    // when the current percentage is 0 set the tooltip value to the current percentage (value 0).
    return currentPercentage ? `${datum.y}` : `${currentPercentage}`;
  };

  // this controls the colors on the progress wheel
  useEffect(() => {
    if (ref && ref.current) {
      const progressBar = (ref.current as HTMLElement).querySelector(
        ".pf-v5-c-chart > svg > g > path:nth-child(1)",
      ) as HTMLElement;

      if (currentPercentage <= 0) {
        progressBar.style.fill = `#AFAFAF`;
      } else if (currentPercentage < 1) {
        progressBar.style.fill = `#DA7600`;
      } else if (currentPercentage === 1) {
        progressBar.style.fill = `#107F47`;
      }
    }
  }, [currentAmount, targetAmount, currentPercentage]);

  if (isLoading) {
    return (
      <div className="LeafProgressCircle">
        <div className="LeafProgressCircle__Loader">
          <LeafCircleLoader isLoading={isLoading} width="100%" height="100%" />
        </div>
      </div>
    );
  }

  const titleValue = formatAboBV(currentAmount, local, user.isoCountryCode);

  const setTitleFontSize = () => {
    if (!titleValue) return 24;
    if (titleValue.length >= 15) return 17;
    if (titleValue.length === 14) return 19;
    if (titleValue.length === 13) return 21;
    if (titleValue.length === 12) return 22;
    return 24;
  };

  const animationSettings = {
    duration: animationDuration,
    onEnter: {
      duration: animationDuration,
    },
    onExit: {
      duration: animationDuration,
    },
  };

  const getChartData = () => {
    if (currentAmount === targetAmount) {
      return [
        { x: labels[0], y: targetAmount },
        { x: labels[1], y: 0 },
      ];
    }

    const currentAmountFill = currentAmount || 0;
    const remainingAmountFill = targetAmount - currentAmount || (currentAmount === 0 ? 1 : 0);

    return [
      { x: labels[0], y: currentAmountFill },
      { x: labels[1], y: remainingAmountFill },
    ];
  };

  return (
    <div className="leaf-progress-circle-container" ref={ref}>
      <div className="leaf-progress-circle-container__text-container">
        <span className="leaf-progress-circle-container__title" style={{ fontSize: `${setTitleFontSize()}px` }}>
          {titleValue}
        </span>
        <span className="leaf-progress-circle-container__subtitle">{subtitle}</span>
      </div>
      <div className="LeafProgressCircle">
        <ChartDonut
          themeColor={ChartThemeColor.skeleton}
          constrainToVisibleArea
          data={getChartData()}
          labels={({ datum }) => renderLabels(datum)}
          labelComponent={
            <ChartTooltip
              style={{ fontSize: 10 }}
              flyoutStyle={{ fill: "#000" }}
              flyoutPadding={4}
              pointerLength={0}
              constrainToVisibleArea={true}
            />
          }
          innerRadius={75}
          cornerRadius={3}
          padAngle={0}
          animate={animationDuration > 0 ? animationSettings : false}
          ariaTitle={ariaTitle}
        />
      </div>
    </div>
  );
};

export default LeafProgressCircle;
